import { constructValue } from '@adornis/baseql/entities/construct.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { filter, tap } from 'rxjs';
import { sessionExtend } from '../../../client/session-timeout.js';
import {
  PersonImportAPI,
  PersonImportStrategyExecutorJob,
  type PersonImportStrategyExecutorResult,
} from '../api/PersonImportAPI.js';
import { openPersonImportResultDialog } from './PersonImportResultDialog.js';
import { openPersonImportStrategyOverviewDialog } from './PersonImportStrategyOverviewDialog.js';

/**
 * Keeps the session alive while the user works through a long import - the overview can take hours.
 */
async function withSessionKeptAlive<T>(run: () => Promise<T>): Promise<T> {
  const interval = setInterval(() => sessionExtend(), 10_000);
  try {
    return await run();
  } finally {
    clearInterval(interval);
  }
}

/**
 * The overview of an import process, then - if the user runs it - the execution and its result. Also the
 * way back into an import that was left open, see `RecentPersonImports`.
 */
export async function reviewPersonImport(processId: string): Promise<void> {
  await withSessionKeptAlive(async () => {
    try {
      await openPersonImportStrategyOverviewDialog(processId);
    } catch {
      // closed without running - the process stays and can be opened again from the recent imports
      return;
    }

    const result = await guardDialog(
      () => PersonImportAPI.executeStrategies(new PersonImportStrategyExecutorJob({ importProcessId: processId }))(),
      { errorMessagePrefix: 'Fehler beim Import', successText: 'Import ausgeführt' },
    );
    if (!result) return;

    const resultParsed = constructValue(JSON.parse(result)) as PersonImportStrategyExecutorResult;
    await openPersonImportResultDialog(processId, resultParsed);
  });
}

export async function startPersonImportFlow() {
  // the file itself rather than only its text: the name is what the recent imports are listed by
  const file = (await askForFile({ acceptFileTypes: '.csv' }))?.[0];
  if (!file) return;
  // the fallback askForTextFile has: Excel saves a csv as windows-1252 unless told otherwise
  const bytes = await file.arrayBuffer();
  let csv: string;
  try {
    csv = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    csv = new TextDecoder('windows-1252').decode(bytes);
  }

  let processId: string | undefined;

  await withSessionKeptAlive(() =>
    guardDialog(
      () =>
        PersonImportAPI.convertCSV(csv, file.name)().pipe(
          // filter out technical updates from csv parser
          filter(m => !m.includes('processing') && !m.includes('transformed headers') && !m.includes('parsed')),
          tap(m => {
            if (m.includes('Finished: ')) {
              processId = m.replace('Finished: ', '');
            }
          }),
        ),
      { errorMessagePrefix: 'Fehler im Import', successText: 'Strategien erfolgreich erstellt' },
    ),
  );

  if (!processId) {
    console.error('No process id found');
    return;
  }

  await reviewPersonImport(processId);
}
